import React, { useState, useCallback } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import {
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';

const FONT_SIZE_OPTIONS = [
  '4px',
  '5px',
  '6px',
  '7px',
  '8px',
  '10px',
  '11px',
  '12px',
  '13px',
  '14px',
  '16px',
  '18px',
  '20px',
  '24px',
  '28px',
  '32px',
  '36px',
  '40px',
  '48px',
];

export const FontSizeSelect: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [fontSize, setFontSize] = useState<string>('16px');

  const applyFontSize = useCallback(
    (newFontSize: string) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            // Apply font size style to the selected text
            $patchStyleText(selection, { 'font-size': newFontSize });
          }
        },
        { discrete: true },
      );
    },
    [editor],
  );

  const handleFontSizeChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newFontSize = event.target.value;
      setFontSize(newFontSize);
      applyFontSize(newFontSize);
    },
    [applyFontSize],
  );

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: '100px',
        marginLeft: '4px',
        marginRight: '4px',
      }}
    >
      <Select
        value={fontSize}
        onChange={handleFontSizeChange}
        displayEmpty
        sx={{
          fontSize: '14px',
          height: 'auto',
          minHeight: '32px',
          backgroundColor: 'white',
          border: '0',
          borderRadius: '6px',
          '& .MuiSelect-select': {
            padding: '2px 32px 2px 8px',
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
            boxSizing: 'border-box',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: '4px',
              marginTop: '0',
              boxShadow: 'none',
              minWidth: '144px',
              backgroundColor: 'white',
              fontSize: '14px',
            },
          },
          MenuListProps: {
            sx: {
              padding: '4px 0',
            },
          },
        }}
        renderValue={value => value || 'Size'}
      >
        {FONT_SIZE_OPTIONS.map(size => (
          <MenuItem
            key={size}
            value={size}
            sx={{
              fontSize: '14px',
              padding: '8px 16px',
              minHeight: 'auto',
              '&:hover': {
                backgroundColor: '#f0f0f3',
              },
              '&.Mui-selected': {
                backgroundColor: '#f0f0f3',
                color: '#1c2024',
                '&:hover': {
                  backgroundColor: '#f0f0f3',
                },
              },
              '&:last-child': {
                borderRadius: '0 0 4px 4px',
              },
            }}
          >
            {size}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
